// AI-CONTEXT: See docs/patterns.md#data-taxonomy
//
// Werkzame mechanismen taxonomie — the "why does it work" layer of the Theory
// of Change, sitting between activiteiten and effecten. Each category has a list
// of observable signals (kenmerken) taken from the source taxonomy. The signals
// are the working definition of a category and are what the AI uses to map an
// organisation's activities onto a mechanism.
//
// Twenty categories: the original twelve (generic, written from a deelnemer in a
// development trajectory) plus eight added for energiearmoede, whose working
// principles are material, financial, informational and system-directed.
// See docs/features/mechanism-taxonomy-energiearmoede.md.
//
// SECTOR SCOPING (ADR-031): every category carries a `sectors` tag. '*' means
// every sector, including future ones. Only authoring surfaces (dropdowns, AI
// prompts, write-path whitelists) narrow via `mechanismCategoriesForSector`;
// anything that RENDERS a stored Category must keep the full list, or a
// cross-sector view loses its Dutch labels. An empty tenant sector list means
// *all*, never *none*.
//
// SYNC: functions/data/mechanismCategories.js is a CommonJS mirror of this file
// (src/ and functions/ are separate npm packages — no shared module boundary).
// Pinned by functions/data/mechanismTaxonomyParity.test.js. Edit both in the
// same commit. See docs/patterns.md#data-mirrored-constants.

// ─── Enums ───────────────────────────────────────────────────────────────────

export const mechanismCategories = [
  // The original generic twelve.
  'self_direction',
  'step_by_step_development',
  'feedback_reflection',
  'personal_guidance',
  'psychological_safety',
  'practice_based_learning',
  'responsibility',
  'social_support',
  'social_connection',
  'inclusion_equality',
  'meaning_contribution',
  'accessibility_alignment',
  // Added for energiearmoede (WM-13 t/m WM-20).
  'material_technical_intervention',
  'financial_relief',
  'burden_relief',
  'consumption_insight',
  'accessible_communication',
  'home_based_support',
  'independent_position',
  'system_collaboration',
]

/**
 * Analysis level a category operates on. Without this distinction, analyses put
 * handelingsprincipes and organisatiecondities on one pile, while the interaction
 * between them ("welke manier van werken werkt onder welke omstandigheden") is
 * the actual research question.
 */
export const mechanismCategoryLevelValues = ['contact', 'organisatie', 'systeem']

// ─── Signals per category (Dutch) ────────────────────────────────────────────

export const mechanismSignalsByCategory = {
  self_direction: [
    'deelnemer kiest activiteiten',
    'deelnemer kiest taken',
    'deelnemer kiest werktempo',
    'deelnemer kiest aantal uren',
    'deelnemer stelt eigen doelen',
    'deelnemer bepaalt eigen vervolgstappen',
    'deelnemer neemt zelf contact op met betrokkenen',
    'vrijwillige deelname',
    'ruimte voor eigen keuzes',
    'deelnemer kiest welke maatregelen worden uitgevoerd',
    'deelnemer mag hulp weigeren zonder gevolgen',
  ],
  step_by_step_development: [
    'starten met beperkte belasting',
    'belasting geleidelijk opbouwen',
    'kleine haalbare doelen stellen',
    'doelen opdelen in kleine stappen',
    'werken met tussenstappen',
    'focus op proces in plaats van resultaat',
    'ruimte voor individueel tempo',
  ],
  feedback_reflection: [
    'voortgang meten',
    'voortgang bespreken',
    'resultaten visueel maken',
    'successen benoemen',
    'groei zichtbaar maken',
    'reflecteren op gedrag',
    'reflecteren op gedachten',
    'reflecteren op gevoelens',
    'bewustwording stimuleren',
  ],
  personal_guidance: [
    'persoonlijke coaching',
    'begeleiding op maat',
    'begeleiding tijdens activiteiten',
    'begeleiding op de werkvloer',
    'regelmatig persoonlijk contact',
    'actief contact houden bij afwezigheid',
    'observeren tijdens uitvoering',
    'doorverwijzen naar aanvullende ondersteuning',
    'vaste begeleider over meerdere contactmomenten',
    'nazorgcontact na afronding',
  ],
  psychological_safety: [
    'niet-veroordelende houding',
    'fouten mogen maken',
    'ruimte om te experimenteren',
    'geen sancties bij terugval',
    'welkom blijven ondanks terugval',
    'vertrouwen uitspreken',
    'deelnemer centraal stellen',
    'onvoorwaardelijke acceptatie',
  ],
  practice_based_learning: [
    'leren door doen',
    'praktijkleren',
    'werken aan echte opdrachten',
    'werken in een echte werkomgeving',
    'oefenen in realistische situaties',
    'direct toepassen van vaardigheden',
  ],
  responsibility: [
    'verantwoordelijkheid voor eigen taken',
    'verantwoordelijkheid voor anderen',
    'verantwoordelijkheden opbouwen',
    'deelnemers begeleiden deelnemers',
    'eigenaarschap over werkzaamheden stimuleren',
    'zelfstandig problemen oplossen',
  ],
  social_support: [
    'ervaringen delen',
    'leren van andere deelnemers',
    'peer learning',
    'deelnemers ondersteunen elkaar',
    'inzetten van ervaringsdeskundigen',
    'lotgenotencontact faciliteren',
    'deelnemers tips laten uitwisselen',
  ],
  social_connection: [
    'samen eten',
    'informele ontmoetingen stimuleren',
    'werken in groepsverband',
    'sociale contacten opbouwen',
    'netwerkvorming stimuleren',
    'mensen aan elkaar verbinden',
    'activiteit op een plek waar deelnemers al komen',
  ],
  inclusion_equality: [
    'gelijkwaardige behandeling',
    'behandelen als volwaardige deelnemer',
    'iedereen doet mee',
    'aansluiten bij mogelijkheden van deelnemer',
    'geen onderscheid maken op achtergrond of problematiek',
    'zowel huurders als eigenaar-bewoners bedienen',
  ],
  meaning_contribution: [
    'nuttig werk verrichten',
    'bijdrage leveren aan organisatie',
    'bijdrage leveren aan gemeenschap',
    'zichtbaar bijdragen aan activiteiten',
    'waardering uitspreken voor bijdrage',
    'deelnemers laten ervaren dat ze nodig zijn',
  ],
  // Narrowed by the ADR-031 split: 'ondersteuning dichtbij organiseren' moved to
  // home_based_support and 'vertrouwen opbouwen' to independent_position. What
  // remains is toeleiding en vindbaarheid.
  accessibility_alignment: [
    'aansluiten bij leefwereld',
    'aansluiten bij interesses',
    'aansluiten bij belastbaarheid',
    'praktische drempels verlagen',
    'outreachend werken',
    'hulp actief aanbieden',
    'proactief benaderen op basis van woning- en wijkdata',
    'aanbellen in plaats van uitnodigen',
    'introductie via een bekende partij',
  ],
  material_technical_intervention: [
    'maatregel direct uitvoeren tijdens het bezoek',
    'benodigde materialen zelf meenemen',
    'maatregelen kosteloos aanbrengen',
    'geen eigen bijdrage of voorschot vragen',
    'apparaten of materialen in eigendom verstrekken',
    'installatie niet overlaten aan de bewoner',
    'techniek correct inregelen',
    'de zuinige stand als standaardinstelling achterlaten',
    'gebreken wegnemen die de bewoner niet zelf kan verhelpen',
    'veiligheidsrisico’s wegnemen',
    'zichtbaar resultaat achterlaten in de woning',
  ],
  financial_relief: [
    'vaste lasten aantoonbaar verlagen',
    'termijnbedrag laten aanpassen aan het werkelijke verbruik',
    'recht op regelingen en toeslagen controleren',
    'niet-gebruikte regelingen actief aanvragen',
    'acute betalingsachterstand helpen oplossen',
    'betalingsregeling of noodfonds inzetten',
    'afsluiting of deurwaarderstraject voorkomen',
    'de besparing in euro’s per maand benoemen',
    'financiële ruimte creëren voordat gedragsverandering wordt gevraagd',
  ],
  burden_relief: [
    'formulieren samen invullen',
    'de aanvraag met toestemming namens de deelnemer doen',
    'benodigde bewijsstukken samen verzamelen',
    'één vast aanspreekpunt bieden',
    'warme overdracht in plaats van doorverwijzen',
    'meegaan naar een instantie',
    'niet opnieuw laten uitleggen wat al bekend is',
    'hulp aanbieden zonder aanvraagprocedure',
    'zelf nabellen in plaats van wachten op de deelnemer',
  ],
  consumption_insight: [
    'meterstanden samen uitlezen',
    'jaarafrekening samen doornemen',
    'verbruik omrekenen naar euro’s',
    'verbruik per apparaat zichtbaar maken',
    'verbruik volgen over de tijd',
    'verbruik vergelijken met vergelijkbare huishoudens',
    'advies afstemmen op de eigen woning en het eigen verbruik',
    'de besparing na een maatregel terugkoppelen',
    'een concreet, klein besparingsdoel afspreken',
  ],
  accessible_communication: [
    'eenvoudige taal gebruiken',
    'beeld en voorbeelden in plaats van tekst',
    'voordoen en samen doen',
    'laten navertellen om begrip te checken',
    'in de eigen taal communiceren of een taalgenoot inzetten',
    'rekening houden met culturele opvattingen',
    'een korte schriftelijke samenvatting op maat achterlaten',
    'geen jargon gebruiken',
  ],
  home_based_support: [
    'ondersteuning bieden in de woning zelf',
    'ondersteuning dichtbij organiseren',
    'de situatie ter plekke observeren in plaats van uitvragen',
    'signaleren wat niet gemeld is',
    'in één bezoek meerdere leefdomeinen bespreken',
    'aansluiten bij een moment waarop de deelnemer er al voor open staat',
    'bezoek plannen op een tijd die de deelnemer schikt',
    'de deelnemer gastheer laten zijn in eigen huis',
    'terugkomen voor een tweede huisbezoek',
  ],
  independent_position: [
    'vertrouwen opbouwen',
    'geen handhavings- of controlerol combineren met hulp',
    'geen gegevens delen zonder toestemming',
    'expliciet benoemen dat deelname geen gevolgen heeft voor uitkering of huur',
    'buurtgenoten of vrijwilligers als uitvoerder inzetten',
    'ervaringsgenoten uit de doelgroep inzetten',
    'langdurig aanwezig blijven in de wijk',
    'herkenbaar en vindbaar zijn in de buurt',
    'aanbevolen worden door een partij die de deelnemer al vertrouwt',
  ],
  system_collaboration: [
    'knelpunten bundelen en terugleggen bij de verantwoordelijke partij',
    'vaste samenwerkingsafspraken met ketenpartners',
    'deelnemers informeren over hun rechten',
    'deelnemers collectief laten optreden richting een derde partij',
    'kleine maatregelen opvolgen met structurele maatregelen',
    'gezamenlijke aanpak per straat, portiek of complex',
    'gebreken terugkoppelen aan de verhuurder',
    'de verhuurder aanspreken op de onderhoudsplicht',
    'afspreken dat de bewoner de besparing houdt',
  ],
}

// ─── Sector tags (ADR-031) ───────────────────────────────────────────────────

// Slugs match meetstandaard-api `effect.sectors[]`; labels live in
// src/helpers/meetstandaard/sectorCatalog.js. '*' = every sector, including
// future ones. Bias towards '*': a wrongly-universal category is noise, a
// wrongly-narrowed one silently removes a working practice from a sector that
// needs it.
export const mechanismCategorySectors = {
  self_direction: ['*'],
  step_by_step_development: ['arbeidsparticipatie', 'energiearmoede'],
  feedback_reflection: ['*'],
  personal_guidance: ['*'],
  psychological_safety: ['*'],
  practice_based_learning: ['arbeidsparticipatie', 'energiearmoede'],
  responsibility: ['arbeidsparticipatie', 'energiearmoede'],
  social_support: ['*'],
  social_connection: ['*'],
  inclusion_equality: ['*'],
  meaning_contribution: ['arbeidsparticipatie', 'energiearmoede'],
  accessibility_alignment: ['*'],
  // Signals are specific to the woning/energie context — a re-integration
  // organisation has no use for a thermostat setting.
  material_technical_intervention: ['energiearmoede'],
  financial_relief: ['energiearmoede'],
  consumption_insight: ['energiearmoede'],
  // Cross-sector: these carry generic signals (some inherited from the
  // categorie-12 split), so narrowing them would strip working practices from
  // arbeidsparticipatie.
  burden_relief: ['*'],
  accessible_communication: ['*'],
  home_based_support: ['*'],
  independent_position: ['*'],
  system_collaboration: ['*'],
}

/**
 * The concrete sector slugs the taxonomy actually references, excluding the '*'
 * wildcard. Drives the sector tab strip so the tabs follow the data instead of a
 * hand-written list that silently hides a newly-tagged category.
 * @type {string[]}
 */
export const mechanismSectorSlugs = [
  ...new Set(
    Object.values(mechanismCategorySectors)
      .flat()
      .filter((s) => s !== '*')
  ),
].sort()

// ─── Analysis level ──────────────────────────────────────────────────────────

// Most categories describe a handeling in the contact between uitvoerder and
// deelnemer. Two do not: independent_position is about mandate and origin of the
// uitvoerder (an organisatiekenmerk), system_collaboration about moving a third
// party (a systeemkenmerk).
export const mechanismCategoryLevels = {
  self_direction: 'contact',
  step_by_step_development: 'contact',
  feedback_reflection: 'contact',
  personal_guidance: 'contact',
  psychological_safety: 'contact',
  practice_based_learning: 'contact',
  responsibility: 'contact',
  social_support: 'contact',
  social_connection: 'contact',
  inclusion_equality: 'contact',
  meaning_contribution: 'contact',
  accessibility_alignment: 'contact',
  material_technical_intervention: 'contact',
  financial_relief: 'contact',
  burden_relief: 'contact',
  consumption_insight: 'contact',
  accessible_communication: 'contact',
  home_based_support: 'contact',
  independent_position: 'organisatie',
  system_collaboration: 'systeem',
}

// ─── Dutch labels ─────────────────────────────────────────────────────────────

export const mechanismCategoryLabels = {
  self_direction: 'Eigen regie',
  step_by_step_development: 'Stapsgewijze ontwikkeling',
  feedback_reflection: 'Feedback & reflectie',
  personal_guidance: 'Persoonlijke begeleiding',
  psychological_safety: 'Psychologische veiligheid',
  practice_based_learning: 'Praktijkleren',
  responsibility: 'Verantwoordelijkheid',
  social_support: 'Sociale steun',
  social_connection: 'Sociale verbinding',
  inclusion_equality: 'Inclusie & gelijkwaardigheid',
  meaning_contribution: 'Zingeving & bijdrage',
  accessibility_alignment: 'Toegankelijkheid & aansluiting',
  material_technical_intervention: 'Materiële & technische ingreep',
  financial_relief: 'Directe financiële verlichting',
  burden_relief: 'Ontzorgen & regeldruk wegnemen',
  consumption_insight: 'Inzicht in verbruik & kosten',
  accessible_communication: 'Begrijpelijke & cultureel passende communicatie',
  home_based_support: 'Werken achter de voordeur',
  independent_position: 'Onafhankelijke en niet-controlerende positie',
  system_collaboration: 'Ketensamenwerking & systeemgerichte actie',
}

// ─── Descriptions (Dutch, summarised from the signals) ───────────────────────

export const mechanismCategoryDescriptions = {
  self_direction:
    'De deelnemer houdt zeggenschap over eigen keuzes, doelen en tempo; deelname is vrijwillig.',
  step_by_step_development:
    'Belasting en doelen worden geleidelijk opgebouwd in kleine, haalbare stappen met ruimte voor individueel tempo.',
  feedback_reflection:
    'Voortgang en groei worden zichtbaar gemaakt en besproken, en de deelnemer reflecteert op gedrag, gedachten en gevoelens.',
  personal_guidance:
    'Persoonlijke, op maat gemaakte begeleiding met regelmatig contact, ook tijdens activiteiten en bij afwezigheid.',
  psychological_safety:
    'Een veilige, niet-veroordelende omgeving waarin fouten en terugval mogen, met onvoorwaardelijke acceptatie.',
  practice_based_learning:
    'Leren door doen in een realistische werkomgeving, met directe toepassing van vaardigheden.',
  responsibility:
    'De deelnemer bouwt verantwoordelijkheid en eigenaarschap op voor eigen taken, anderen en het oplossen van problemen.',
  social_support:
    'Deelnemers steunen en leren van elkaar via peer learning, lotgenotencontact en ervaringsdeskundigen.',
  social_connection:
    'Sociale contacten en netwerken worden opgebouwd door samen dingen te doen en mensen te verbinden.',
  inclusion_equality:
    'Iedereen doet mee en wordt gelijkwaardig en als volwaardig behandeld, ongeacht achtergrond of problematiek.',
  meaning_contribution:
    'De deelnemer ervaart zin en waardering door een zichtbare, nuttige bijdrage te leveren.',
  accessibility_alignment:
    'De deelnemer wordt gevonden en actief benaderd, en praktische drempels naar de organisatie worden verlaagd.',
  material_technical_intervention:
    'Het probleem wordt fysiek weggenomen in plaats van bij de bewoner gelegd: maatregelen worden ter plekke en kosteloos uitgevoerd.',
  financial_relief:
    'Vaste lasten en achterstanden worden direct verlaagd, zodat bestaanszekerheid een voorwaarde is en niet pas een resultaat.',
  burden_relief:
    'De organisatie neemt handelingen richting derden over — formulieren, aanvragen en overdrachten — die de deelnemer anders niet doet.',
  consumption_insight:
    'Het eigen verbruik en de eigen rekening worden inzichtelijk gemaakt en vergeleken, als aangrijpingspunt voor gedrag.',
  accessible_communication:
    'De communicatie sluit aan bij taal, leesvaardigheid en culturele opvattingen van de deelnemer.',
  home_based_support:
    'De woning is vindplaats en werkplaats: de situatie wordt ter plekke geobserveerd en meerdere leefdomeinen komen in één bezoek samen.',
  independent_position:
    'Vertrouwen ontstaat door mandaat en herkomst van de uitvoerder: geen controlerol, herkenbaar en langdurig aanwezig in de wijk.',
  system_collaboration:
    'De partij die de oorzaak in de hand heeft — verhuurder, gemeente, leverancier — wordt aangesproken en knelpunten worden gebundeld teruggelegd.',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Whether the given key is a known mechanism category.
 * @param {string} key - The category key to check.
 * @returns {boolean}
 */
export const isMechanismCategory = (key) => mechanismCategories.includes(key)

/**
 * The observable signals (kenmerken) for a mechanism category.
 * @param {string} key - The category key.
 * @returns {string[]} The signal list, or an empty array for an unknown key.
 */
export const getMechanismSignals = (key) => mechanismSignalsByCategory[key] || []

/**
 * Every observable signal (kenmerk) across all categories, de-duped + sorted.
 * Used to suggest mechanism titles via a <datalist>.
 * @returns {string[]}
 */
export const getAllMechanismSignals = () => {
  const set = new Set()
  Object.values(mechanismSignalsByCategory).forEach((list) =>
    (Array.isArray(list) ? list : []).forEach((s) => set.add(s))
  )
  return [...set].sort()
}

/**
 * Whether a category applies to every sector (the '*' tag).
 *
 * Derived from the mirrored `mechanismCategorySectors` data, so it needs no
 * mirror of its own. Exported because two rendering surfaces ask the same
 * question — the sector chip and the picker's "voor alle sectoren" split — and a
 * third inline copy of `tags.includes('*')` is how the rule starts to drift
 * (docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook).
 *
 * @param {string} key - Taxonomy category key.
 * @returns {boolean}
 */
export const isUniversalCategory = (key) => {
  const tags = mechanismCategorySectors[key]
  return Array.isArray(tags) && tags.includes('*')
}

/**
 * Whether a category's sector tag matches one of the tenant's sectors.
 * @param {string[]} tags - The category's `sectors` tag.
 * @param {string[]} sectors - The tenant's sector slugs.
 * @returns {boolean}
 */
const sectorMatches = (tags, sectors) => {
  if (!Array.isArray(tags)) return false
  if (tags.includes('*')) return true
  return sectors.some((s) => tags.includes(s))
}

/**
 * The categories an organisation in the given sectors may choose from.
 *
 * Use this for AUTHORING surfaces only — dropdowns, AI prompts, write-path
 * whitelists. Anything that renders a stored Category must keep using the full
 * `mechanismCategories`, or a cross-sector view loses its labels (ADR-031).
 *
 * An empty or missing sector list returns everything: a tenant that never picked
 * a sector must not end up with an empty dropdown.
 *
 * @param {string[]} [sectors] - Sector slugs, typically CompagnyMeta.SelectedSectors.
 * @returns {string[]} Category keys in canonical order.
 */
export const mechanismCategoriesForSector = (sectors) => {
  const list = Array.isArray(sectors) ? sectors.filter(Boolean) : []
  if (list.length === 0) return [...mechanismCategories]
  return mechanismCategories.filter((key) =>
    sectorMatches(mechanismCategorySectors[key], list)
  )
}

// ─── Aggregate default export ─────────────────────────────────────────────────

const mechanismTaxonomy = {
  mechanismCategories,
  mechanismSignalsByCategory,
  mechanismCategoryLabels,
  mechanismCategoryDescriptions,
  mechanismCategorySectors,
  mechanismCategoryLevels,
  mechanismCategoryLevelValues,
  mechanismSectorSlugs,
  isMechanismCategory,
  isUniversalCategory,
  getMechanismSignals,
  mechanismCategoriesForSector,
}

export default mechanismTaxonomy
